import axios from "axios";

/**
 * Resolve API base URL.
 *
 * Priority:
 *  1. process.env.REACT_APP_BACKEND_URL — set at build time on Render Static
 *     Site (will be https://richild-api.onrender.com in production).
 *  2. Fallback to window.location.origin for local dev / SSR safety.
 */
function resolveApiBase() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl) {
    return envUrl.replace(/\/+$/, "");
  }
  if (typeof window !== "undefined") {
    return window.location.origin;
  }
  return "";
}

const BASE = resolveApiBase();
export const API = `${BASE}/api`;

/**
 * Convert a backend-relative URL (e.g. "/api/media/...") to an absolute URL
 * usable in <img>/<video> src attributes. External http(s) URLs are returned as-is.
 */
export function resolveMediaUrl(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE}${url.startsWith("/") ? url : "/" + url}`;
}

export const api = axios.create({
  baseURL: API,
  timeout: 20000, // 20s - generous for cold starts via Vercel proxy
});

// Attach token if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("rci_admin_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Retry interceptor — automatically retries failed requests.
 *
 * Why: when the Vercel edge proxies /api/* to the Emergent backend, the
 * upstream Cloudflare layer occasionally returns a transient 5xx, an HTML
 * challenge page, or a connection reset. The user sees "Something went wrong"
 * intermittently even though the backend is healthy. Retrying once or twice
 * with a small backoff fixes 99% of these flakes invisibly.
 *
 * Special case: during a Vercel redeploy (a few seconds window), API requests
 * can briefly fall through the SPA catch-all rewrite, returning the React
 * `index.html` with HTTP 200 OR 404. Axios will then fail to parse JSON.
 * We detect this by checking if the response content-type starts with text/html
 * and retry with a longer delay to let the new deployment settle.
 *
 * We DO NOT retry:
 *  - real 4xx client errors (bad credentials, validation, real 404 with JSON)
 *  - aborted requests (user navigated away)
 *  - explicit no-retry config (config.__noRetry = true)
 */
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 800;
const DEPLOY_WINDOW_DELAY_MS = 2500; // longer delay if HTML returned during deploy

function isHtmlResponse(resp) {
  if (!resp) return false;
  const ct = resp.headers?.["content-type"] || resp.headers?.get?.("content-type") || "";
  return /^text\/html/i.test(ct);
}

api.interceptors.response.use(
  async (response) => {
    // Sneaky case: 200 OK but the body is the React index.html (Vercel SPA
    // fallback caught the request during a redeploy). Treat as retryable.
    if (isHtmlResponse(response)) {
      const cfg = response.config;
      cfg.__retryCount = cfg.__retryCount || 0;
      if (!cfg.__noRetry && cfg.__retryCount < MAX_RETRIES) {
        cfg.__retryCount += 1;
        await new Promise((r) => setTimeout(r, DEPLOY_WINDOW_DELAY_MS));
        return api(cfg);
      }
    }
    return response;
  },
  async (error) => {
    const config = error.config;
    if (!config || config.__noRetry) return Promise.reject(error);

    const resp = error.response;
    const status = resp?.status;
    const isNetworkErr = !resp;
    const is5xx = status >= 500 && status < 600;
    // 404 that returns HTML = Vercel SPA fallback during deploy, retry it.
    const isDeployFallback404 = status === 404 && isHtmlResponse(resp);
    const isRetryable = isNetworkErr || is5xx || isDeployFallback404;

    config.__retryCount = config.__retryCount || 0;
    if (!isRetryable || config.__retryCount >= MAX_RETRIES) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;
    const delay = isDeployFallback404
      ? DEPLOY_WINDOW_DELAY_MS * config.__retryCount
      : RETRY_DELAY_MS * config.__retryCount;
    await new Promise((r) => setTimeout(r, delay));
    return api(config);
  }
);

/**
 * Extract a user-friendly error message from an axios error.
 * Falls back to network-aware messages so users see WHY the request failed,
 * instead of a generic "Something went wrong".
 */
export function describeApiError(err, fallback) {
  if (!err) return fallback || "Erreur inconnue.";

  // Backend returned a structured error (FastAPI HTTPException)
  const detail = err.response?.data?.detail;
  if (detail != null) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
        .filter(Boolean);
      if (msgs.length) return msgs.join(" ");
    }
    if (typeof detail.msg === "string") return detail.msg;
  }

  // No response at all — network / CORS / blocker / timeout
  if (!err.response) {
    if (err.code === "ECONNABORTED") return "Délai d'attente dépassé. Vérifiez votre connexion.";
    if (err.message && /network/i.test(err.message)) return "Erreur réseau. Vérifiez votre connexion Internet.";
    return "Impossible de joindre le serveur. Réessayez dans quelques secondes.";
  }

  // Generic by HTTP status
  const status = err.response.status;
  if (status === 401) return "Identifiants invalides.";
  if (status === 403) return "Accès refusé.";
  if (status === 404) return "Ressource introuvable.";
  if (status === 429) return "Trop de tentatives. Patientez un instant.";
  if (status >= 500) return `Erreur serveur (${status}). Réessayez dans un instant.`;
  return fallback || `Erreur ${status}.`;
}

// Backwards-compatible alias
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
